import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, PackageSearch, FileDown } from "lucide-react";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const Row = ({ label, value, muted, positive, bold }) => (
  <div className={`flex justify-between ${bold ? "text-base" : "text-sm"} mb-1.5`}>
    <span className={`font-mono uppercase tracking-wide text-xs ${bold ? "text-[#F5F5F0]" : "text-[#52525B]"}`}>{label}</span>
    <span className={`font-mono ${positive ? "text-emerald-400" : bold ? "text-[#D4AF37] text-lg" : "text-[#E8E8E3]"}`}>{value}</span>
  </div>
);

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [state, setState] = useState("polling"); // polling | paid | failed
  const [ref, setRef] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);
  const sid = params.get("session_id");

  useEffect(() => {
    if (!sid) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        setRef(data.order_ref);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          try { const rc = await axios.get(`${API}/payments/receipt/${sid}`); setReceipt(rc.data); } catch (e) { /* non-blocking */ }
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      if (attempts.current >= 12) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid, clear]);

  return (
    <main className="pt-32 pb-32 min-h-[70vh]" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl mx-auto px-6">
        {state === "polling" && (
          <div className="text-center pt-8">
            <Loader2 size={48} className="mx-auto mb-6 text-[#D4AF37] animate-spin" />
            <h1 className="font-serif text-4xl font-light mb-3">Confirming payment…</h1>
            <p className="text-[#71717A]">One moment while we verify your card payment.</p>
          </div>
        )}

        {state === "paid" && (
          <>
            <div className="text-center mb-10">
              <CheckCircle2 size={52} className="mx-auto mb-5 text-emerald-400" />
              <h1 className="font-serif text-5xl font-light mb-3" data-testid="payment-paid">Payment confirmed</h1>
              <p className="text-[#A1A1AA]">Thank you — your order {ref && <span className="text-[#D4AF37] font-mono">{ref}</span>} is confirmed.</p>
              <p className="text-[#71717A] text-sm mt-1">A confirmation email is on its way. We ship same-day for orders placed before noon.</p>
            </div>

            {receipt && (
              <div className="border border-zinc-800 bg-[#0C0C0C] p-7" data-testid="receipt-card">
                <div className="flex items-center justify-between mb-5 pb-4 border-b border-zinc-900">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525B]">Receipt</p>
                    <p className="font-serif text-2xl font-light mt-1">{receipt.order_ref}</p>
                  </div>
                  <div className="text-right font-mono text-[10px] text-[#71717A] leading-relaxed">
                    <p>{(receipt.created_at || "").slice(0, 10)}</p>
                    <p>{receipt.card_last4 ? `${(receipt.card_brand || "Card")} ····${receipt.card_last4}` : "Card · Stripe"}</p>
                    <p className="text-emerald-400 uppercase tracking-wide">Paid</p>
                  </div>
                </div>

                <ul className="mb-5 border-b border-zinc-900 pb-4 space-y-2.5" data-testid="receipt-items">
                  {receipt.items.map((it) => (
                    <li key={it.id} className="flex justify-between text-sm gap-3">
                      <span className="text-[#A1A1AA] truncate">{it.name} <span className="text-[#52525B]">({it.size}) ×{it.qty}</span></span>
                      <span className="font-mono text-[#F5F5F0] shrink-0">{money(it.lineTotal)}</span>
                    </li>
                  ))}
                </ul>

                <Row label="Subtotal" value={money(receipt.subtotal)} />
                {receipt.bulk_savings > 0 && <Row label="Bulk savings" value={`-${money(receipt.bulk_savings)}`} positive />}
                {receipt.promo_discount > 0 && <Row label={`Promo ${receipt.promo_code || ""}`} value={`-${money(receipt.promo_discount)}`} positive />}
                {receipt.shipping_fee > 0
                  ? <Row label="Shipping" value={money(receipt.shipping_fee)} />
                  : <Row label="Shipping" value="Free" positive />}
                <Row label="Sales tax" value={money(receipt.tax_collected)} />
                <div className="mt-3 pt-3 border-t border-zinc-900">
                  <Row label="Total paid" value={money(receipt.amount_charged)} bold />
                </div>

                {receipt.shipping && (
                  <p className="mt-5 font-mono text-[11px] text-[#52525B] leading-relaxed">
                    Ship to: {receipt.shipping.name}, {receipt.shipping.line1}{receipt.shipping.line2 ? ` ${receipt.shipping.line2}` : ""}, {receipt.shipping.city} {receipt.shipping.state} {receipt.shipping.zip}
                  </p>
                )}

                <a href={`${API}/payments/receipt/${sid}/pdf`} target="_blank" rel="noreferrer" data-testid="download-receipt-btn"
                  className="mt-6 w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-black py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">
                  <FileDown size={15} /> Download PDF Receipt
                </a>
              </div>
            )}

            <div className="flex gap-3 justify-center mt-8">
              <Link to="/track" data-testid="success-track" className="inline-flex items-center gap-2 border border-zinc-700 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"><PackageSearch size={14} /> Track order</Link>
              <Link to="/" className="inline-flex items-center gap-2 bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-colors">Continue browsing</Link>
            </div>
          </>
        )}

        {state === "failed" && (
          <div className="text-center pt-8">
            <XCircle size={56} className="mx-auto mb-6 text-red-400" />
            <h1 className="font-serif text-4xl font-light mb-3">Payment not confirmed</h1>
            <p className="text-[#71717A] mb-10">We couldn&apos;t verify your payment yet. If you were charged, contact support and we&apos;ll sort it out.</p>
            <Link to="/checkout" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">Try again</Link>
          </div>
        )}
      </motion.div>
    </main>
  );
}
